/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable camelcase */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import PulseLoader from 'react-spinners/PulseLoader';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// MaterialUI
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import Rating from '@material-ui/lab/Rating';
import Tooltip from '@material-ui/core/Tooltip';
import NoSsr from '@material-ui/core/NoSsr';

import { COMMENT_RULES } from 'constants/index';

// app: components
import DisplayNameChooser from 'components/DisplayNameChooser';
import RichTextField from 'components/RichTextField';
import ImageUploader from 'components/ImageUploader';

// assets
import CssCommentForm from './CommentForm.scss';

const MIN_LENGTH = COMMENT_RULES.review.minLength;

const ReviewForm = React.forwardRef((props, ref) => {
  const {
    didCook,
    focused,
    isOpen,
    onBlur,
    onChange,
    onChangeTab,
    onClose,
    onFocus,
    onOpen,
    onSubmit,
    optionsMenu,
    rating,
    review,
    saveInProgress,
    saveError,
    showAsEditButton,
    onNameSubmit,
    isEditingName,
    setIsEditingName,
    disabledNameField,
    isModerator,
    comment,
    uploadingError,
    setUploadingError,
    isUploadingImage,
    setReviewImage,
    onRemoveExistingImage,
  } = props;

  useStyles(CssCommentForm);
  const tooltipTimerRef = useRef();
  const [isDirty, setDirty] = useState(false);
  const [demoRating, setDemoRating] = useState(null);
  const [reviewLength, setReviewLength] = useState(0);
  const [showRatingReminder, setShowRatingReminder] = useState(false);

  const displayType = useSelector(state => state.dimensions.displayType);

  const handleReviewChange = htmlContent => {
    if (!isOpen) {
      onOpen(htmlContent);
    }
    onChange({ review: htmlContent, rating });
  };

  const handleRatingChange = (event, value) => {
    setDemoRating(value);
    setShowRatingReminder(false);
    onChange({ review, rating: value });
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (isEditingName) {
      toast.error('You are currently editing your display name');

      return;
    }
    onSubmit({
      didCook: true,
      review,
      rating,
      id: comment && comment?.id,
      is_new: !comment || !comment?.id,
    });
  };

  const remindAboutRating = () => {
    if (!rating) {
      setShowRatingReminder(true);

      for (let i = 1; i <= 5; i += 1) {
        // eslint-disable-next-line no-loop-func
        setTimeout(() => setDemoRating(i), 75 * i);
      }
      for (let i = 1; i <= 5; i += 1) {
        // eslint-disable-next-line no-loop-func
        setTimeout(() => setDemoRating(5 - i), 250 + 75 * i);
      }
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
      tooltipTimerRef.current = setTimeout(() => setShowRatingReminder(false), 5000);
    }
  };

  const handleClose = () => {
    onChange({ review: null, rating: null });
    setDirty(false);
    setShowRatingReminder(false);
    setDemoRating(null);
    onClose();
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
  };

  const handleBlur = event => {
    setDirty(!!reviewLength);
    onBlur(event);
  };

  /**
   * If the “Please select a rating “ tooltip is showing,
   * it should disappear when I go into Edit my name mode.
   * */
  useEffect(
    () => {
      if (!isEditingName) {
        return;
      }

      setShowRatingReminder(false);
    },
    [isEditingName],
  );

  const saveButtonText = showAsEditButton ? 'Update review' : 'Post review';

  const handleImageSelected = file => {
    setUploadingError(null);

    setReviewImage(file);
  };

  const handleImageValidationError = error => {
    console.error('validate image error = ', error);
    setUploadingError(null);
  };

  if (!isOpen && showAsEditButton) {
    return (
      <div className="comment-form--as-button comment-form__row comment-form__centered margin-top-10">
        <Button
          className="comment-form__activator"
          color="primary"
          size="large"
          variant="outlined"
          onClick={onOpen}
          fullWidth
        >
          Edit your review
        </Button>
      </div>
    );
  }

  return (
    <form noValidate className="comment-form" onSubmit={handleSubmit}>
      <Collapse in={isOpen} timeout={300}>
        <NoSsr>
          <div className="comment-form__row">
            <DisplayNameChooser
              canEditName
              isEditingName={isEditingName}
              disabledNameField={disabledNameField}
              setIsEditingName={setIsEditingName}
              onNameSubmit={onNameSubmit}
            />
            <div className="comment-form__stars">
              <Tooltip
                arrow
                disableHoverListener={isEditingName}
                disableTouchListener={isEditingName}
                disableFocusListener={isEditingName}
                placement={displayType === 'mobile' ? 'bottom' : 'right'}
                open={showRatingReminder}
                title="Please add a rating"
              >
                <Rating
                  disabled={isEditingName}
                  value={demoRating || rating}
                  name="rating"
                  precision={1}
                  size="large"
                  onChange={handleRatingChange}
                />
              </Tooltip>
            </div>
          </div>
        </NoSsr>
      </Collapse>
      <RichTextField
        ref={ref}
        focused={focused}
        isDirty={isDirty}
        isEditingName={isEditingName}
        handleReviewChange={handleReviewChange}
        review={review}
        setReviewLength={setReviewLength}
        onOpen={onOpen}
        isOpen={isOpen}
        isModerator={isModerator}
        handleBlur={handleBlur}
        placeholder="Your review"
        onFocus={onFocus}
      />
      <Collapse in={isOpen} timeout={300}>
        <NoSsr>
          {optionsMenu ? <div className="comment-form__dots-menu">{optionsMenu}</div> : null}
        </NoSsr>
      </Collapse>
      <ImageUploader
        className="comment-form__img-uploader"
        isOpen={isOpen}
        existingImage={comment?.image_urls ? comment?.image_urls[0] : null}
        uploadError={uploadingError}
        isUploading={isUploadingImage}
        onImageSelected={handleImageSelected}
        onRemoveExistingImage={onRemoveExistingImage}
        onValidationError={handleImageValidationError}
      />
      <Collapse in={isOpen} timeout={300}>
        <NoSsr>
          <div className="comment-form__row comment-form__buttons">
            <Button
              className="comment-form__button"
              disabled={isEditingName}
              onClick={handleClose}
              size="large"
              variant="contained"
            >
              Cancel
            </Button>
            <span onClick={remindAboutRating} onMouseOver={remindAboutRating}>
              <Button
                className="comment-form__button"
                color="primary"
                disabled={
                  isUploadingImage ||
                  isEditingName ||
                  reviewLength < MIN_LENGTH ||
                  !rating ||
                  saveInProgress
                }
                size="large"
                type="submit"
                variant="contained"
              >
                <PulseLoader
                  loading={saveInProgress || isUploadingImage}
                  color="#fff"
                  sizeUnit="px"
                  size={10}
                />
                {saveInProgress || isUploadingImage ? '' : saveButtonText}
              </Button>
            </span>
          </div>
          <div className="comment-form__row comment-form__centered margin-top-40">
            {didCook || saveError ? null : (
              <Alert severity="info">
                Didn&apos;t cook this yet?{' '}
                <button
                  className="link link--energized"
                  onClick={event => onChangeTab(event, 'question')}
                >
                  Ask a question
                </button>{' '}
                or{' '}
                <button
                  className="link link--energized"
                  onClick={event => onChangeTab(event, 'note')}
                >
                  write a private note
                </button>{' '}
                instead.
              </Alert>
            )}
            {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          </div>
        </NoSsr>
      </Collapse>
    </form>
  );
});

ReviewForm.propTypes = {
  didCook: PropTypes.bool,
  focused: PropTypes.bool,
  isOpen: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  comment: PropTypes.any,
  onChangeTab: PropTypes.func,
  onClose: PropTypes.func,
  onFocus: PropTypes.func,
  onOpen: PropTypes.func,
  onSubmit: PropTypes.func,
  onNameSubmit: PropTypes.func,
  setUploadingError: PropTypes.func,
  setReviewImage: PropTypes.func,
  onRemoveExistingImage: PropTypes.func,
  optionsMenu: PropTypes.node,
  rating: PropTypes.number,
  review: PropTypes.string,
  uploadingError: PropTypes.string,
  saveInProgress: PropTypes.bool,
  saveError: PropTypes.string,
  showAsEditButton: PropTypes.bool,
  isEditingName: PropTypes.bool,
  disabledNameField: PropTypes.bool,
  setIsEditingName: PropTypes.func,
  isModerator: PropTypes.bool,
  isUploadingImage: PropTypes.bool,
};

ReviewForm.defaultProps = {
  didCook: false,
  focused: false,
  isOpen: false,
  comment: null,
  disabledNameField: false,
  onBlur: () => {},
  setUploadingError: () => {},
  onRemoveExistingImage: () => {},
  setReviewImage: () => {},
  onChange: () => {},
  onChangeTab: () => {},
  onClose: () => {},
  onFocus: () => {},
  onOpen: () => {},
  onSubmit: () => {},
  onNameSubmit: () => {},
  optionsMenu: null,
  rating: null,
  uploadingError: null,
  review: '',
  saveInProgress: false,
  saveError: null,
  showAsEditButton: false,
  isEditingName: false,
  isModerator: false,
  isUploadingImage: false,
  setIsEditingName: () => {},
};

export default React.memo(ReviewForm);
